import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Drawer, Typography, Switch, IconButton, Divider,
  CircularProgress, Alert, Button, Checkbox, FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Setting2 } from 'iconsax-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Team, TeamSpaceItem } from '../../types/team.type';
import { getTeamSpaces } from '../../services/team.service';
import { useTeamStore } from '../../store/team.store';
import { useToast, TOAST_TYPES } from '../../providers/ToastProvider';

interface TeamSettingsDrawerProps {
  team: Team;
  open: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

function parseSpacesResponse(res: unknown): TeamSpaceItem[] {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object' && 'data' in res) {
    const data = (res as { data: unknown }).data;
    return Array.isArray(data) ? data : [];
  }
  return [];
}

function arraysEqual(a: string[], b: string[]) {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((v, i) => v === sortedB[i]);
}

export default function TeamSettingsDrawer({ team, open, onClose, onUpdated }: TeamSettingsDrawerProps) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { editTeam } = useTeamStore();
  const isRTL = i18n.dir() === 'rtl';

  const [spaces, setSpaces] = useState<TeamSpaceItem[]>([]);
  const [spacesLoading, setSpacesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [googleError, setGoogleError] = useState(false);

  const [draftIncludeManager, setDraftIncludeManager] = useState(team.include_manager_tasks);
  const [draftAllowedSpaces, setDraftAllowedSpaces] = useState<string[]>(team.allowed_space_names ?? []);

  const loadSpaces = useCallback(() => {
    setSpacesLoading(true);
    getTeamSpaces(team.id)
      .then((res: unknown) => setSpaces(parseSpacesResponse(res)))
      .catch(() => setSpaces([]))
      .finally(() => setSpacesLoading(false));
  }, [team.id]);

  const resetDraft = useCallback(() => {
    setDraftIncludeManager(team.include_manager_tasks);
    setDraftAllowedSpaces(team.allowed_space_names ?? []);
    setGoogleError(false);
  }, [team.include_manager_tasks, team.allowed_space_names]);

  useEffect(() => {
    if (open) {
      resetDraft();
      loadSpaces();
    }
  }, [open, resetDraft, loadSpaces]);

  const hasChanges = useMemo(() => (
    draftIncludeManager !== team.include_manager_tasks
    || !arraysEqual(draftAllowedSpaces, team.allowed_space_names ?? [])
  ), [draftIncludeManager, draftAllowedSpaces, team.include_manager_tasks, team.allowed_space_names]);

  const handleClose = () => {
    resetDraft();
    onClose();
  };

  const handleSave = async () => {
    setSaving(true);
    setGoogleError(false);
    try {
      await editTeam(team.id, {
        include_manager_tasks: draftIncludeManager,
        allowed_space_names: draftAllowedSpaces,
      });
      showToast(t('teams.settingsSaved'), TOAST_TYPES.SUCCESS);
      onUpdated?.();
      loadSpaces();
      onClose();
    } catch (e: any) {
      const detail = e.response?.data?.detail || '';
      if (typeof detail === 'string' && detail.toLowerCase().includes('google')) {
        setGoogleError(true);
      }
      showToast(t('common.error'), TOAST_TYPES.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const isSpaceSelected = (spaceName: string) =>
    draftAllowedSpaces.length === 0 || draftAllowedSpaces.includes(spaceName);

  const getEffectiveSelectedCount = () => {
    const allNames = spaces.map(s => s.space_name);
    return draftAllowedSpaces.length === 0 ? allNames.length : draftAllowedSpaces.length;
  };

  const toggleSpace = (spaceName: string) => {
    const allNames = spaces.map(s => s.space_name);
    const isAllSelected = draftAllowedSpaces.length === 0;
    const currentlySelected = isSpaceSelected(spaceName);

    if (currentlySelected && getEffectiveSelectedCount() <= 1) {
      showToast(t('teams.minOneSpaceRequired'), TOAST_TYPES.WARNING);
      return;
    }

    let next: string[];

    if (isAllSelected) {
      next = allNames.filter(n => n !== spaceName);
    } else if (draftAllowedSpaces.includes(spaceName)) {
      next = draftAllowedSpaces.filter(n => n !== spaceName);
    } else {
      next = [...draftAllowedSpaces, spaceName];
    }

    if (next.length === allNames.length) next = [];
    setDraftAllowedSpaces(next);
  };

  const selectAllSpaces = () => setDraftAllowedSpaces([]);

  return (
    <Drawer
      anchor={isRTL ? 'left' : 'right'}
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 400 },
          maxWidth: '100vw',
          borderRadius: isRTL ? '0 16px 16px 0' : '16px 0 0 16px',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Setting2 size={20} color="#3B82F6" />
            <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{t('teams.settingsTitle')}</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
            {t('teams.settingsSectionTasks')}
          </Typography>
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{t('teams.includeMyTasks')}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                  {t('teams.includeMyTasksDesc')}
                </Typography>
              </Box>
              <Switch
                checked={draftIncludeManager}
                onChange={(_, checked) => setDraftIncludeManager(checked)}
                disabled={saving}
                color="primary"
                size="small"
              />
            </Box>
            {googleError && (
              <Alert
                severity="warning"
                sx={{ mt: 1.5, borderRadius: 2 }}
                action={
                  <Button component={RouterLink} to="/dashboard/settings" size="small" color="inherit">
                    {t('tasks.connectGoogle')}
                  </Button>
                }
              >
                {t('teams.googleRequiredForInclude')}
              </Alert>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('teams.settingsSectionSpaces')}
            </Typography>
            {draftAllowedSpaces.length > 0 && (
              <Button size="small" onClick={selectAllSpaces} disabled={saving} sx={{ fontSize: '0.75rem', minWidth: 'auto' }}>
                {t('teams.selectAllSpaces')}
              </Button>
            )}
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 1.5 }}>
            {t('teams.allowedSpacesDesc')}
          </Typography>

          {spacesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : spaces.length === 0 ? (
            <Box sx={{ py: 3, px: 2, borderRadius: 2, bgcolor: '#F8FAFC', textAlign: 'center' }}>
              <Typography sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>
                {t('teams.noSpacesAvailable')}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ borderRadius: 2.5, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
              {spaces.map((space, i) => {
                const selected = isSpaceSelected(space.space_name);
                const label = space.source === 'manager'
                  ? `${space.space_name} (${t('teams.spaceFromManager')})`
                  : space.space_name;
                return (
                  <Box key={space.space_name}>
                    {i > 0 && <Divider />}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selected}
                          onChange={() => toggleSpace(space.space_name)}
                          disabled={saving}
                          size="small"
                          sx={{ color: '#CBD5E1', '&.Mui-checked': { color: '#3B82F6' } }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: selected ? 600 : 400 }}>
                          {label}
                        </Typography>
                      }
                      sx={{
                        mx: 0, px: 1.5, py: 0.75, width: '100%',
                        bgcolor: selected ? '#F0F9FF' : 'transparent',
                        '&:hover': { bgcolor: selected ? '#E0F2FE' : '#F8FAFC' },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* Footer — save once */}
        <Box sx={{
          px: 2.5, py: 2, borderTop: '1px solid #F1F5F9',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5,
          bgcolor: hasChanges ? '#FFFBEB' : '#fff',
        }}>
          {hasChanges && (
            <Typography sx={{ fontSize: '0.75rem', color: '#D97706', fontWeight: 500 }}>
              {t('teams.unsavedChanges')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
            <Button onClick={handleClose} disabled={saving} sx={{ color: 'text.secondary', textTransform: 'none' }}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || !hasChanges}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
              sx={{ textTransform: 'none', minWidth: 88 }}
            >
              {t('common.save')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}

/** Compact button + active-settings summary for the team header */
export function TeamSettingsTrigger({
  team,
  onClick,
}: {
  team: Team;
  onClick: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      {team.include_manager_tasks && (
        <Box sx={{ px: 1.25, py: 0.4, borderRadius: '999px', bgcolor: '#F0F9FF', border: '1px solid #BFDBFE' }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: '#1D4ED8' }}>
            {t('teams.myTasksIncluded')}
          </Typography>
        </Box>
      )}
      <Button
        variant="outlined"
        size="small"
        startIcon={<Setting2 size={16} color="#3B82F6" />}
        onClick={onClick}
        sx={{
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.8125rem',
          fontWeight: 600,
          borderColor: '#BFDBFE',
          color: '#3B82F6',
          bgcolor: '#F8FAFC',
          '&:hover': { bgcolor: '#EFF6FF', borderColor: '#93C5FD' },
        }}
      >
        {t('teams.openSettings')}
      </Button>
    </Box>
  );
}
